import { useEffect, useRef } from 'react'
import { DataMatrixElement } from '@/types'
import { useSettingsStore } from '@/stores/settingsStore'
import { useLabelStore } from '@/stores/labelStore'
import bwipjs from 'bwip-js'

interface DataMatrixElementPreviewProps {
  element: DataMatrixElement
  zoom: number
  mmToPx: number
}

export function DataMatrixElementPreview({ element, zoom, mmToPx }: DataMatrixElementPreviewProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const { variableData } = useSettingsStore()
  const { label } = useLabelStore()

  // Get display content
  let content = element.content
  if (element.isVariable && element.variableName) {
    content = variableData[element.variableName] || element.content
  }

  // DataMatrix size calculation:
  // In ZPL ^BX, the size parameter sets module size in dots
  // A typical DataMatrix for short content has ~12-16 modules per side
  // We need to calculate scale to match ZPL output
  const dotsPerMm = label.dpi / 25.4
  const estimatedModules = 14
  const sizeInDots = element.size * estimatedModules
  const sizeInMm = sizeInDots / dotsPerMm
  // Calculate scale factor to achieve target size in pixels
  const targetSizePx = Math.max(20, sizeInMm * mmToPx * zoom)
  // bwip-js scale is approximately 2px per module at scale=1
  const scale = Math.max(1, targetSizePx / (estimatedModules * 2))

  useEffect(() => {
    if (!canvasRef.current || !content) return

    try {
      bwipjs.toCanvas(canvasRef.current, {
        bcid: 'datamatrix',
        text: content,
        scale: scale,
      })
    } catch (e) {
      // Invalid DataMatrix data - show placeholder
      const ctx = canvasRef.current.getContext('2d', { willReadFrequently: true })
      if (ctx) {
        canvasRef.current.width = targetSizePx
        canvasRef.current.height = targetSizePx
        ctx.fillStyle = '#f0f0f0'
        ctx.fillRect(0, 0, targetSizePx, targetSizePx)
        ctx.fillStyle = '#666'
        ctx.font = `${10 * zoom}px Arial`
        ctx.textAlign = 'center'
        ctx.fillText('DM', targetSizePx / 2, targetSizePx / 2)
      }
    }
  }, [content, scale, targetSizePx, zoom])

  return <canvas ref={canvasRef} />
}
